import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ReturnTypeEnum } from "@redotech/redo-model/return";
import { folderTypes } from "@redotech/redo-model/views/folders";
import {
  ClaimView,
  ReturnView,
  ViewTypesNames,
  WarrantyView,
} from "@redotech/redo-model/views/views";
import { Context, useContext, useEffect, useMemo, useState } from "react";

interface BaseNavData {
  teamViews?: ClaimView[] | ReturnView[] | WarrantyView[];
  folders?: folderTypes["returns"][];
}

interface ViewOrderingConfig<TNavData extends BaseNavData> {
  returnType: ReturnTypeEnum;
  viewType:
    | ViewTypesNames.RETURNS
    | ViewTypesNames.CLAIMS
    | ViewTypesNames.WARRANTIES
    | ViewTypesNames.RECYCLE;
  navDataContext: Context<TNavData | undefined>;
  reloadNavDataContext: Context<(() => Promise<void>) | undefined>;
}

export function useViewOrderingService<TNavData extends BaseNavData>(
  config: ViewOrderingConfig<TNavData>,
) {
  const { returnType, viewType, navDataContext, reloadNavDataContext } = config;

  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const navData = useContext(navDataContext);
  const reloadNavData = useRequiredContext(reloadNavDataContext);

  const [views, setViews] = useState<
    ClaimView[] | ReturnView[] | WarrantyView[]
  >([]);
  const [folders, setFolders] = useState<folderTypes["returns"][]>([]);
  const [notificationCounts, setNotificationCounts] = useState<
    Record<string, number>
  >({});

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showEditFolderModal, setShowEditFolderModal] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<
    folderTypes["returns"] | null
  >(null);

  const handleCreateFolder = useHandler(async (name: string) => {
    await rpcClient.createReturnViewFolder({ name, type: returnType });
    void reloadNavData();
  });

  const handleEditFolder = useHandler(
    async (folderId: string, folderName: string) => {
      await rpcClient.updateReturnViewFolderName({
        folderId,
        name: folderName,
      });
      void reloadNavData();
    },
  );

  const handleDeleteFolder = useHandler(async (folderId: string) => {
    await rpcClient.deleteReturnViewFolder({ folderId, type: returnType });
    void reloadNavData();
  });

  const fetchNotificationCounts = useHandler(async () => {
    try {
      const viewsToFetch = views.filter(
        (view) => view.showNotification || view.hideWhenEmpty,
      );

      if (viewsToFetch.length === 0) {
        setNotificationCounts({});
        return;
      }

      const counts = await rpcClient.getReturnViewNotificationCounts({
        viewIds: viewsToFetch.map((view) => view._id),
        type: viewType,
      });

      setNotificationCounts(counts.counts);
    } catch (error) {
      console.error("Failed to fetch notification counts:", error);
      setNotificationCounts({});
    }
  });

  const toggleViewNotifications = useHandler(async (viewId: string) => {
    const view = views.find((v) => v._id === viewId);
    if (!view) return;

    const updatedView = { ...view, showNotification: !view.showNotification };

    await rpcClient.saveReturnView({ view: updatedView, type: returnType });

    await reloadNavData();
    void fetchNotificationCounts();
  });

  const updateOrdering = useHandler(
    async (
      updatedViews: ClaimView[] | ReturnView[] | WarrantyView[],
      updatedFolders: folderTypes[keyof folderTypes][],
    ) => {
      setViews(updatedViews);
      setFolders(updatedFolders);

      await rpcClient.saveReturnViewsAndFolders({
        views: updatedViews,
        folders: updatedFolders,
        type: returnType,
      });
    },
  );

  const saveOrdering = useHandler(async () => {
    try {
      await rpcClient.saveReturnViewsAndFolders({
        views,
        folders,
        type: returnType,
      });
    } catch (error) {
      void reloadNavData();
    }
  });

  useEffect(() => {
    if (navData?.teamViews && navData?.folders) {
      setViews(navData.teamViews);
      setFolders(navData.folders);
    }
  }, [navData]);

  useEffect(() => {
    if (views.length > 0) {
      void fetchNotificationCounts();
    }
  }, [views, fetchNotificationCounts]);

  const openCreateFolderModal = useHandler(() => {
    setShowCreateFolderModal(true);
  });

  const openEditFolderModal = useHandler((folderId: string) => {
    const folder = navData?.folders?.find((f) => f._id === folderId);
    if (folder) {
      setShowEditFolderModal(true);
      setSelectedFolder(folder);
    }
  });

  const reservedFolderNames = useMemo(() => {
    return new Set(folders.map((folder) => folder.name));
  }, [folders]);

  return {
    views,
    folders,
    notificationCounts,
    showCreateFolderModal,
    showEditFolderModal,
    selectedFolder,
    reservedFolderNames,
    handleCreateFolder,
    handleEditFolder,
    handleDeleteFolder,
    updateOrdering,
    saveOrdering,
    toggleViewNotifications,
    openCreateFolderModal,
    openEditFolderModal,
    setShowCreateFolderModal,
    setShowEditFolderModal,
  };
}
